import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '三上悠亞官方粉絲頁',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首頁', item: 'https://luxiangba.cn/' },
      { '@type': 'ListItem', position: 2, name: '分類瀏覽', item: 'https://luxiangba.cn/category' },
      { '@type': 'ListItem', position: 3, name: article.category, item: `https://luxiangba.cn/category?tab=${article.categorySlug}` },
      { '@type': 'ListItem', position: 4, name: article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首頁', path: '/' },
              { label: '分類瀏覽', path: '/category' },
              { label: article.category, path: `/category?tab=${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Link
                to={`/category?tab=${article.categorySlug}`}
                className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors duration-200 mb-4 cursor-pointer"
              >
                {article.category}
              </Link>
              <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  閱讀時間約 {article.readTime}
                </span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.imageAlt}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="article-content text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">標籤：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200/70"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-6">
                相關文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.id} article={related} variant="featured" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}